import { ThemeConfig, theme } from 'antd';

const sharedTokens = {
  borderRadius: 6,
  fontSize: 14,
  lineHeight: 22 / 14,
  controlHeight: 32,
  fontFamily:
    "'SF Pro Text', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
};

export const antdLightTheme: ThemeConfig = {
  // Expose all antd theme tokens as CSS variables (e.g. `--ant-color-fill`)
  // so styles outside the React tree (Tailwind utilities, static CSSProperties
  // objects, plain CSS) can reach for the same palette antd components use.
  cssVar: true,
  // Disable antd's hash-suffix on classnames so global selectors (e.g. the
  // Menu overrides in `index.css`) keep matching after enabling cssVar.
  hashed: false,
  token: {
    ...sharedTokens,
    colorPrimary: '#24252C',
    colorPrimaryHover: '#6A6D81',
    colorPrimaryActive: '#3C3D49',
    colorPrimaryBg: '#E9E9ED',
    colorPrimaryBgHover: '#DBDBE1',
    colorPrimaryBorder: '#BEBECA',
    colorPrimaryBorderHover: '#8F91A3',
    colorPrimaryText: '#24252C',
    colorPrimaryTextHover: '#535565',
    colorPrimaryTextActive: '#3C3D49',
    colorInfo: '#24252C',
    colorInfoBg: '#E9E9ED',
    colorInfoBorder: '#BEBECA',
    colorInfoHover: '#6A6D81',
    colorInfoActive: '#3C3D49',
    colorInfoText: '#24252C',
    colorInfoTextHover: '#535565',
    colorBgSpotlight: '#ffffff',
    colorTextLightSolid: '#ffffff',
    colorLink: '#1677ff',
    colorLinkHover: '#4096ff',
    colorLinkActive: '#0958d9',
  },
  components: {
    Tooltip: {
      colorBgSpotlight: 'rgba(0, 0, 0, 0.85)',
      colorTextLightSolid: '#fff',
    },
    Button: {
      colorPrimary: '#24252C',
      colorPrimaryHover: '#6A6D81',
      colorPrimaryActive: '#3C3D49',
      primaryShadow: 'none',
      borderRadius: 8,
      defaultBorderColor: '#d9d9d9',
      defaultShadow: '0px 2px 0px 0px rgba(0,0,0,0.02)',
      paddingContentHorizontal: 15,
      colorLink: '#3B99F6',
      colorLinkHover: '#2775CF',
      colorLinkActive: '#1856A8',
      // Unify text-mode and default-mode hover backgrounds with the shared
      // overlay-hover token so the Studio toolbar's antd `<Button type="text">`
      // (Cursor / Add room / etc.) lands on the same hover colour as the
      // sidebar Menu and the toolbar's raw `<button>`s.
      textHoverBg: 'var(--color-overlay-hover)',
      defaultHoverBg: 'var(--color-overlay-hover)',
    },
    Checkbox: {
      colorPrimary: '#24252C',
      colorPrimaryHover: '#6A6D81',
      colorPrimaryBorder: '#BEBECA',
      borderRadiusSM: 4,
    },
    DatePicker: {
      colorPrimary: '#24252C',
      colorPrimaryHover: '#6A6D81',
      colorPrimaryActive: '#3C3D49',
      borderRadius: 6,
      colorTextPlaceholder: '#A8AAB8',
      colorBorder: '#d9d9d9',
      colorText: 'rgba(0, 0, 0, 0.88)',
      colorTextSecondary: 'rgba(0, 0, 0, 0.88)',
      colorLink: '#24252C',
      colorLinkHover: '#535565',
      colorBgContainer: '#ffffff',
      controlItemBgActive: '#E9E9ED',
      controlItemBgHover: '#F1F1F4',
    },
    Modal: {
      borderRadiusLG: 8,
      boxShadow:
        '0px 6px 16px 0px rgba(0,0,0,0.08),0px 3px 6px -4px rgba(0,0,0,0.12),0px 9px 28px 8px rgba(0,0,0,0.05)',
    },
    Select: {
      optionActiveBg: '#E9E9ED',
      optionSelectedBg: '#E9E9ED',
      optionSelectedFontWeight: 600,
      selectorBg: '#ffffff',
      colorBorder: '#d9d9d9',
      colorText: 'rgba(0, 0, 0, 0.88)',
      colorTextPlaceholder: '#A8AAB8',
      borderRadius: 4,
      controlHeight: 30,
      fontSize: 14,
      colorPrimaryHover: '#6A6D81',
      colorPrimaryBorderHover: '#8F91A3',
      colorPrimaryBorder: '#BEBECA',
    },
    Tabs: {
      horizontalItemPadding: '8px 0',
      horizontalItemPaddingSM: '6px 0',
    },
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      // Unselected and directly-selected items use the primary text color;
      // only the ancestor SubMenus leading to the selection render in red
      // (see index.css). Hover + selected pill route through the shared
      // overlay-hover token so the sidebar Menu, Studio toolbar buttons,
      // and dropdown rows all land on the same hover colour in both modes.
      itemColor: 'var(--color-text-primary)',
      itemHoverColor: 'var(--color-text-primary)',
      itemSelectedColor: 'var(--color-text-primary)',
      itemHoverBg: 'var(--color-overlay-hover)',
      itemSelectedBg: 'var(--color-overlay-hover)',
    },
  },
};

export const antdDarkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  cssVar: true,
  hashed: false,
  token: {
    ...sharedTokens,
    colorPrimary: '#F9F9FA',
    colorPrimaryHover: '#DBDBE1',
    colorPrimaryActive: '#F1F1F4',
    colorPrimaryBg: '#535565',
    colorPrimaryBgHover: '#6A6D81',
    colorPrimaryBorder: '#8F91A3',
    colorPrimaryBorderHover: '#BEBECA',
    colorPrimaryText: '#F9F9FA',
    colorPrimaryTextHover: '#E9E9ED',
    colorPrimaryTextActive: '#F1F1F4',
    colorInfo: '#F9F9FA',
    colorInfoBg: '#535565',
    colorInfoBorder: '#8F91A3',
    colorInfoHover: '#DBDBE1',
    colorInfoActive: '#F1F1F4',
    colorInfoText: '#F9F9FA',
    colorInfoTextHover: '#E9E9ED',
    // Antd's `darkAlgorithm` derives many tokens numerically from these
    // base colors, so they must be concrete hex strings (not `var()` —
    // that breaks the algorithm and produces unstable derived tokens
    // that re-render on every theme read, triggering infinite update
    // loops in components that use `useImperativeHandle` like Tooltip).
    // Values mirror the dashboard's `--color-bg-*` dark palette in
    // `src/index.css` — when shifting one, shift the other in lockstep.
    colorBgContainer: '#2c2d33',
    colorBgElevated: '#3a3b44',
    colorBgLayout: '#1a1b21',
    colorBgSpotlight: '#3a3b44',
    // `colorBorder` stays at `#535565` (more contrast than the dashboard's
    // `--color-antd-border` = `#3a3b44`) deliberately: antd uses this for
    // input field / control borders where strong contrast keeps the field
    // boundaries discoverable. `--color-antd-border` is for sidebar / panel
    // dividers where a calmer line reads better. The two diverged after
    // Phase B; keeping that divergence intentional.
    colorBorder: '#535565',
    // Mirrors `--color-border-subtle` dark — was `#3C3D49` (the OLD
    // surface-raised tier), which after Phase B was inverted-semantic
    // (brighter than `colorBorder` despite being "secondary").
    colorBorderSecondary: '#34353c',
    colorText: '#F9F9FA',
    colorTextSecondary: '#A8AAB8',
    colorTextTertiary: '#8F91A3',
    colorTextQuaternary: '#6A6D81',
    colorTextLightSolid: '#24252C',
    colorLink: '#4096ff',
    colorLinkHover: '#69b1ff',
    colorLinkActive: '#1677ff',
  },
  components: {
    Tooltip: {
      colorBgSpotlight: 'rgba(255, 255, 255, 0.95)',
      colorTextLightSolid: 'rgba(0, 0, 0, 0.88)',
    },
    Button: {
      colorPrimary: '#F9F9FA',
      colorPrimaryHover: '#DBDBE1',
      colorPrimaryActive: '#F1F1F4',
      primaryShadow: 'none',
      borderRadius: 8,
      defaultBorderColor: '#535565',
      defaultShadow: 'none',
      paddingContentHorizontal: 15,
      colorLink: '#5EA8E8',
      colorLinkHover: '#89C5F3',
      colorLinkActive: '#3585D4',
      // Same unification as the light Button block — antd `<Button type="text">`
      // hover lands on the shared translucent overlay instead of antd's default
      // `colorFillTertiary`, matching the sidebar Menu and raw-button hovers.
      textHoverBg: 'var(--color-overlay-hover)',
      defaultHoverBg: 'var(--color-overlay-hover)',
    },
    Checkbox: {
      colorPrimary: '#F9F9FA',
      colorPrimaryHover: '#DBDBE1',
      colorPrimaryBorder: '#8F91A3',
      colorBgContainer: '#3C3D49',
      colorBorder: '#535565',
      borderRadiusSM: 4,
    },
    DatePicker: {
      colorPrimary: '#F9F9FA',
      colorPrimaryHover: '#DBDBE1',
      colorPrimaryActive: '#F1F1F4',
      borderRadius: 6,
      colorTextPlaceholder: '#6A6D81',
      colorBorder: '#535565',
      colorText: '#F9F9FA',
      colorTextSecondary: '#A8AAB8',
      colorLink: '#F9F9FA',
      colorLinkHover: '#E9E9ED',
      colorBgContainer: '#24252C',
      colorBgElevated: '#3C3D49',
      activeBorderColor: '#BEBECA',
      hoverBorderColor: '#BEBECA',
      controlItemBgActive: '#535565',
      controlItemBgHover: '#6A6D81',
    },
    Modal: {
      borderRadiusLG: 8,
      // Stronger dark-mode shadow than antd's default (which uses ~0.2 alpha
      // — invisible against the deep page bg). Combined with the 1px outline
      // on `colorBorderSecondary`, this gives Modals + Drawers + Popovers
      // visible elevation on the dark canvas.
      boxShadow:
        '0 12px 32px 0 rgba(0,0,0,0.6), 0 4px 8px -2px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04)',
    },
    Select: {
      // Route active + selected option highlights through the unified
      // hover token (translucent overlay). Was `#535565` — the OLD hover
      // tier, now too pronounced against the deeper dropdown bg
      // (`colorBgElevated` = `#3a3b44`). The translucent overlay lifts
      // ~10–12 units off whatever surface the dropdown portals onto,
      // matching the rest of the redesign's hover surfaces.
      optionActiveBg: 'var(--color-overlay-hover)',
      optionSelectedBg: 'var(--color-overlay-hover)',
      optionSelectedFontWeight: 600,
      selectorBg: '#24252C',
      colorBorder: '#535565',
      colorText: '#F9F9FA',
      colorTextPlaceholder: '#6A6D81',
      borderRadius: 4,
      controlHeight: 30,
      fontSize: 14,
      colorPrimaryHover: '#DBDBE1',
      colorPrimaryBorderHover: '#BEBECA',
      colorPrimaryBorder: '#8F91A3',
    },
    Tabs: {
      itemColor: '#A8AAB8',
      itemHoverColor: '#F9F9FA',
      itemSelectedColor: '#F9F9FA',
      inkBarColor: '#F9F9FA',
      horizontalItemPadding: '8px 0',
      horizontalItemPaddingSM: '6px 0',
    },
    Input: {
      colorBgContainer: '#24252C',
      colorText: '#F9F9FA',
      colorTextPlaceholder: '#6A6D81',
      colorBorder: '#535565',
      activeBorderColor: '#BEBECA',
      hoverBorderColor: '#BEBECA',
    },
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: '#F9F9FA',
      itemHoverColor: '#F9F9FA',
      itemSelectedColor: '#F9F9FA',
      // Route hover + selected through `--color-overlay-hover` so the
      // sidebar Menu hover lands on the same translucent overlay as the
      // Studio toolbar's `hover:bg-overlay-hover` Tailwind utility.
      itemHoverBg: 'var(--color-overlay-hover)',
      itemSelectedBg: 'var(--color-overlay-hover)',
      groupTitleColor: '#A8AAB8',
    },
  },
};

/** @deprecated Use antdLightTheme or antdDarkTheme instead */
export const antdTheme = antdLightTheme;
